import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import emailjs from '@emailjs/browser';
import {
	FaPaperPlane,
	FaCheckCircle,
	FaSpinner,
} from 'react-icons/fa';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const ContactForm = () => {
	const { t } = useTranslation();
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitSuccess, setSubmitSuccess] = useState(false);

	const onSubmit = async (data) => {
		setIsSubmitting(true);

		try {
			await emailjs.send(
				SERVICE_ID,
				TEMPLATE_ID,
				{
					name: data.name,
					email: data.email,
					subject: data.subject,
					message: data.message,
				},
				PUBLIC_KEY
			);

			setSubmitSuccess(true);
			reset();
			setTimeout(() => setSubmitSuccess(false), 5000);
		} catch (error) {
			console.error('EmailJS error:', error);
			alert(
				t('contact.errorMessage') ||
					'Something went wrong. Please try again.'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	if (submitSuccess) {
		return (
			<div className="bg-green-50 text-green-800 p-6 rounded-lg text-center">
				<FaCheckCircle className="text-4xl mx-auto mb-4" />
				<h3 className="text-xl font-semibold mb-2">
					{t('contact.successTitle')}
				</h3>
				<p>{t('contact.successMessage')}</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
			<div>
				<label
					htmlFor="name"
					className="block text-sm font-medium text-gray-700 mb-1"
				>
					{t('contact.nameLabel')} *
				</label>
				<input
					id="name"
					type="text"
					name="name"
					{...register('name', {
						required: t('contact.requiredField'),
					})}
					className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
				/>
				{errors.name && (
					<p className="mt-1 text-sm text-red-600">
						{errors.name.message}
					</p>
				)}
			</div>

			<div>
				<label
					htmlFor="email"
					className="block text-sm font-medium text-gray-700 mb-1"
				>
					{t('contact.emailLabel')} *
				</label>
				<input
					id="email"
					type="email"
					{...register('email', {
						required: t('contact.requiredField'),
						pattern: {
							value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
							message: t('contact.invalidEmail'),
						},
					})}
					className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
				/>
				{errors.email && (
					<p className="mt-1 text-sm text-red-600">
						{errors.email.message}
					</p>
				)}
			</div>

			<div>
				<label
					htmlFor="subject"
					className="block text-sm font-medium text-gray-700 mb-1"
				>
					{t('contact.subjectLabel')} *
				</label>
				<select
					id="subject"
					name="subject"
					{...register('subject', {
						required: t('contact.requiredField'),
					})}
					className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
				>
					<option value="">{t('contact.selectOption')}</option>
					<option value="general">
						{t('contact.generalInquiry')}
					</option>
					<option value="scholarship">
						{t('contact.scholarshipQuestion')}
					</option>
					<option value="course">
						{t('contact.courseQuestion')}
					</option>
					<option value="other">{t('contact.other')}</option>
				</select>
				{errors.subject && (
					<p className="mt-1 text-sm text-red-600">
						{errors.subject.message}
					</p>
				)}
			</div>

			<div>
				<label
					htmlFor="message"
					className="block text-sm font-medium text-gray-700 mb-1"
				>
					{t('contact.messageLabel')} *
				</label>
				<textarea
					id="message"
					name="message"
					rows={5}
					{...register('message', {
						required: t('contact.requiredField'),
					})}
					className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
				/>
				{errors.message && (
					<p className="mt-1 text-sm text-red-600">
						{errors.message.message}
					</p>
				)}
			</div>

			<button
				type="submit"
				disabled={isSubmitting}
				className="w-full flex items-center justify-center bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
			>
				{isSubmitting ? (
					<>
						<FaSpinner className="animate-spin mr-2" />
						{t('contact.submitting')}
					</>
				) : (
					<>
						<FaPaperPlane className="mr-2" />
						{t('contact.submitButton')}
					</>
				)}
			</button>
		</form>
	);
};

export default ContactForm;
